import styled from "@emotion/styled"
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js"

import { useLocale } from "next-intl"
import { extractProperty } from "utils"

import { Doughnut } from "react-chartjs-2"
import { NewText, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"

ChartJS.register(ArcElement, Tooltip, Legend)

const StyledDiv = styled.div`
  height: 400px;
  display: flex;
  gap: 120px;
  margin-top: 60px;
  display: flex;
  align-items: end;
  ${(props) => props.theme.breakpoints.down("sm")} {
    flex-direction: column;
    height: auto;
    margin-top: 0px;
    gap: 32px;
  }
`

const StyledTableDiv = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50%;
    gap: 40px;
  }
`

const StyledDot = styled.div<{ $background: string }>`
  width: 13px;
  height: 13px;
  border-radius: 50%;
  background-color: ${(p) => p.$background};
  border: 1px solid black;
  margin-right: 8px;
`

const StyledTextWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  display: flex;
  justify-content: space-between;
  height: 52px;
  align-items: center;
  border-bottom: 1px solid #f7f7f7;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 58px;
  }
`

const StyledDonutDiv = styled.div`
  width: 100%;
  height: 330px;
  align-items: flex-end;
  margin-top: 20px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 50%;
    height: 450px;
  }
`

type Props = {
  data: any
  commentLine: string
  tooltipRef?: any
}

function TzabarPie({ data, commentLine, tooltipRef }: Props) {
  const countryNames = data.map((item: any) => extractProperty(item, "country"))
  const color = data.map((item: any) => "#" + extractProperty(item, "color"))
  const total = data.map((item: any) => extractProperty(item, "total"))
  const locale = useLocale()
  const isDesktop = useIsDesktop()

  const pieData = {
    datasets: [
      {
        data: total,
        labels: countryNames,
        backgroundColor: color,
        borderColor: "white",
        borderWidth: 6,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,

    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    cutout: "70%",
    hover: {
      mode: null, // Disable hover effect on entire dataset
    },
    layout: {
      padding: 20,
    },
    plugins: {
      tooltip: {
        enabled: false, // Disable the default tooltip

        external: function (context: {
          tooltip: any
          chart: { canvas: { getBoundingClientRect: () => any } }
        }) {
          let tooltipEl = document.getElementById("chartjs-tooltip")

          if (!tooltipEl) {
            tooltipEl = document.createElement("div")
            tooltipEl.id = "chartjs-tooltip"
            tooltipEl.style.width = "164px"
            tooltipEl.style.height = "64px"
            tooltipEl.style.backgroundColor = "white"
            tooltipEl.style.padding = "10px"
            tooltipEl.style.height = "auto"
            tooltipEl.style.borderRadius = "5px"
            tooltipEl.innerHTML = `<div id='tooltip-content'></div>`
            tooltipEl.style.boxShadow = "2px 2px 6px rgba(0, 0, 0, 0.2)"
            tooltipRef.current = tooltipEl
            document.body.appendChild(tooltipEl)
          }

          const tooltipModel = context.tooltip
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0"
            return
          }

          tooltipEl.classList.remove("above", "below", "no-transform")
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign)
          } else {
            tooltipEl.classList.add("no-transform")
          }

          const body = tooltipModel.body[0]?.lines[0] || ""
          const labelIndex = tooltipModel.dataPoints[0]?.dataIndex
          const labelName = countryNames[labelIndex] || "" // Get label name

          const innerHtml = `
          <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: start; justify-content: center;">
          <div style="display: flex; align-items: center; margin-bottom: 5px;">
            <div style="background-color: ${
              tooltipModel.labelColors[0]?.backgroundColor || "transparent"
            }; width: 8px; height: 8px; border-radius: 50%; margin-left: ${
            locale === "he" ? "10px" : "0px"
          }; margin-right: ${locale === "he" ? "0px" : "10px"}"></div>
            <div style=" font-weight: bold; width: calc(100% - 16px)">${labelName}</div>
          </div>
          <div>${body}MW</div>
        </div>
          `

          tooltipEl.innerHTML = innerHtml

          const position = context.chart.canvas.getBoundingClientRect()
          tooltipEl.style.opacity = "1"
          tooltipEl.style.position = "absolute"
          tooltipEl.style.zIndex = "1"
          if (isDesktop)
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + "px"
          else
            tooltipEl.style.left =
              position.left + window.pageXOffset + tooltipModel.caretX / 2 + "px"
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + "px"
          tooltipEl.style.pointerEvents = "none"
        },
      },
    },
  } as any

  return (
    <StyledDiv>
      <StyledDonutDiv>
        <Doughnut data={pieData} options={chartOptions} />
      </StyledDonutDiv>
      <StyledTableDiv>
        {countryNames.map((item: any, index: number) => (
          <StyledItem key={index.toString()}>
            <StyledTextWrapper>
              <StyledDot $background={color[index]} />
              <NewText
                component={"p"}
                desktopFontSize={18}
                desktopLineHeight={"26px"}
                desktopFontWeight={400}
                mobileFontSize={16}
                mobileLineHeight={"23px"}
                color={sharedColors.black}
              >
                {item}
              </NewText>
            </StyledTextWrapper>
            <NewText
              component={"p"}
              desktopFontSize={18}
              desktopLineHeight={"26px"}
              desktopFontWeight={400}
              mobileFontSize={16}
              mobileLineHeight={"23px"}
              color={sharedColors.black}
            >
              {total[index].toLocaleString("he-IL")}MW
            </NewText>
          </StyledItem>
        ))}
        <NewText
          component={"p"}
          desktopFontSize={14}
          desktopLineHeight={"20px"}
          desktopFontWeight={400}
          mobileFontSize={12}
          mobileLineHeight={"17px"}
          color={sharedColors.black}
        >
          {commentLine}
        </NewText>
      </StyledTableDiv>
    </StyledDiv>
  )
}

export default TzabarPie
